import { useEffect, useMemo, useRef, useState } from 'react';
import isNil from 'lodash/isNil';
/**
 * useValue 受控 & 非受控 value 逻辑封装
 * @param props 组件的props
 * @param defaultValue 默认value
 * @param options
 * @param options.valueName value 在 props 上的名称
 * @param options.fitValue 将外界传入的 value 进行转化
 * @param options.compare 用于对比两个 value 是否相同（返回 true 表示相同）
 * @return [value, setValue, isControlled]
 */
export function useValue(props, defaultValue, options) {
    const { valueName = 'value', fitValue, compare } = options || {};
    const defaultName = useMemo(() => {
        const n = valueName.substr(0, 1).toUpperCase() + valueName.substr(1);
        return `default${n}`;
    }, [valueName]);
    const isControlled = valueName in props;
    const defaultVal = useMemo(() => {
        let v = defaultValue;
        if (defaultName in props) {
            v = props[defaultName];
        }
        if (isControlled) {
            v = props[valueName];
        }
        if (isNil(v)) {
            v = defaultValue;
        }
        if (fitValue) {
            v = fitValue(v);
        }
        return v;
    }, []);
    const getControlledValue = () => {
        const x = props[valueName];
        if (isNil(x)) {
            if (fitValue) {
                return fitValue(defaultValue);
            }
            return defaultValue;
        }
        if (fitValue) {
            return fitValue(x);
        }
        return x;
    };
    const [value, setValue] = useState(defaultVal);
    const propValue = useRef(defaultVal);
    // 同步 prop 值
    useEffect(() => {
        if (isControlled) {
            const newValue = getControlledValue();
            const isSame = compare ? compare(newValue, propValue.current) : newValue === propValue.current;
            if (!isSame) {
                propValue.current = newValue;
                setValue(newValue);
            }
        }
    }, [props[valueName], props.dataSource]);
    // 同时修改 state 和 prop 值
    const setPropValue = (val) => {
        const isSame = compare ? compare(val, propValue.current) : val === propValue.current;
        if (!isSame) {
            propValue.current = val;
            setValue(val);
        }
    };
    if (isControlled) {
        // 受控模式
        return [propValue.current, setValue, isControlled, setPropValue];
    }
    return [value, setValue, isControlled, setPropValue];
}
